import Card from '@raydiant/raydial/components/Card';
import Page from '../../components/Page';
import PageLayout from '../../components/PageLayout/PageLayout';
import PageTitle from '../../components/PageLayout/PageTitle';
import TextField from '@raydiant/raydial/components/TextField';
import Button from '@raydiant/raydial/components/Button';
import history from '../../history';
import * as paths from '../../routes/paths';
import { useState } from 'react';
import {
  DeleteIntegrationConfirmationModal,
  Footer,
  Header,
  LocationGuidTextFields,
  SiteUidTextFields,
  Site,
} from './components';
import LoadingModal from '@raydiant/raydial/components/LoadingModal';
import Text from '@raydiant/raydial/components/Text';
import useUpdateConnection from '../../hooks/useUpdateConnection';
import useDeleteConnection from '../../hooks/useDeleteConnection';
import Modal from '@raydiant/raydial/components/Modal';
import Dialog, {
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import { Connection } from '@raydiant/api-client-js';
import { useToastState } from '@raydiant/raydial/components/Toast';

type ProductHubEditToastIntegrationProps = {
  connection: Connection;
  type: string;
};

type IntegrationTypes = {
  restaurantGuids?: string[];
  sites?: Site[];
};

const ProductHubEditGuidIntegrationPage = (
  props: ProductHubEditToastIntegrationProps,
) => {
  const state = useToastState();
  const { connection, type } = props;
  const settings = connection.settings as IntegrationTypes;
  const [formData, setFormData] = useState({
    name: connection.name,
    locationGuids: type === 'toast' ? settings.restaurantGuids : [],
    sites: type === 'qsr' ? settings.sites : [],
  });
  const [showEditConfirmationModal, setShowEditConfirmationModal] =
    useState(false);

  const [
    isDeleteIntegrationConfirmationModalOpen,
    setIsDeleteIntegrationConfirmationModalOpen,
  ] = useState(false);
  const updateConnectionQuery = useUpdateConnection();
  const deleteConnectionQuery = useDeleteConnection();
  const { mutateAsync: deleteConnection } = deleteConnectionQuery;
  const { mutateAsync: updateConnection } = updateConnectionQuery;
  const isValid = formData.name.length > 0;

  let integrationType = '',
    integrationTypeDescription = '';
  if (type === 'toast') {
    integrationType = 'Toast';
    integrationTypeDescription = 'location GUIDs';
  }
  if (type === 'qsr') {
    integrationType = 'QSR';
    integrationTypeDescription = 'Sites';
  }

  const editConnection = async () => {
    const settingsDetails =
      type === 'toast'
        ? { restaurantGuids: formData.locationGuids }
        : type === 'qsr' && formData.sites
        ? {
            sites: formData.sites.map(({ uid, name }) => ({ uid, name })),
          }
        : {};

    await updateConnection({
      id: connection.id,
      params: {
        name: formData.name,
        settings: settingsDetails,
      },
    });

    state.add({
      title: 'Integration Updated',
      description: 'Integration settings have been updated successfully',
    });
    history.push(paths.integrations());
  };

  const handleSubmitConfirmationModal = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    editConnection();
  };

  const handleDeleteIntegration = () => {
    setIsDeleteIntegrationConfirmationModalOpen(true);
  };

  const handleOnNameChange = (name: string) => {
    setFormData({ ...formData, name });
  };

  const handleOnLocationGuidsChange = (locationGuids: Array<string>) => {
    setFormData({ ...formData, locationGuids });
  };

  const handleOnSiteChange = (sites: Array<Site>) => {
    setFormData({ ...formData, sites });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const siteRemoved =
      type === 'toast' && settings.restaurantGuids
        ? !settings.restaurantGuids.every((g) =>
            formData?.locationGuids?.includes(g),
          )
        : type === 'qsr' && settings.sites
        ? !settings.sites.every((g) => formData?.sites?.includes(g))
        : false;

    if (siteRemoved) {
      setShowEditConfirmationModal(true);
      return;
    }

    editConnection();
  };

  const renderEditConfirmationModal = () => {
    const onCloseConfirmationModal = () => {
      setShowEditConfirmationModal(false);
    };

    return (
      <Modal
        isOpen={showEditConfirmationModal}
        onOpenChange={onCloseConfirmationModal}
      >
        <Dialog showCloseButton>
          <form onSubmit={handleSubmitConfirmationModal}>
            <DialogHeader>
              <DialogHeading>Confirm changes</DialogHeading>
            </DialogHeader>

            <div className="px-4 mb-8 flex mt-4 flex-col">
              <Text className="mb-4">
                {`Removing ${integrationTypeDescription} may affect your presentations and
                content playing on your devices and kiosks.`}
              </Text>
              <Text>Are you sure you want to continue?</Text>
            </div>

            <footer className="flex flex-wrap gap-6 pb-4 px-4">
              <Button
                size="large"
                variant="secondary"
                className="grow"
                onPress={onCloseConfirmationModal}
              >
                Cancel
              </Button>
              <Button
                size="large"
                variant="primary"
                className="grow"
                type="submit"
              >
                Confirm
              </Button>
            </footer>
          </form>
        </Dialog>
      </Modal>
    );
  };

  const onCloseDeleteIntegrationConfirmationModal = () => {
    setIsDeleteIntegrationConfirmationModalOpen(false);
  };

  const deleteIntegration = async () => {
    await deleteConnection(connection.id);
    setIsDeleteIntegrationConfirmationModalOpen(false);
    state.add({
      title: 'Integration Deleted',
      description: `${integrationType} integration deleted successfully`,
    });
    history.push(paths.integrations());
  };

  return (
    <Page>
      <PageLayout title={<PageTitle title="Integrations" />}>
        <LoadingModal
          isOpen={
            updateConnectionQuery.isLoading || deleteConnectionQuery.isLoading
          }
        />
        <DeleteIntegrationConfirmationModal
          onClose={onCloseDeleteIntegrationConfirmationModal}
          isOpen={isDeleteIntegrationConfirmationModalOpen}
          onDelete={deleteIntegration}
          connectionName={connection.name}
        />
        {renderEditConfirmationModal()}
        <Card className="text-black max-w-[628px]">
          <Header
            heading="Edit Integration Settings"
            subheading={`Manage ${integrationType} integration ${integrationTypeDescription}`}
          />
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <TextField
                label="Integration Name"
                placeholder="Enter integration name"
                description="This could be your restaurant or brand, eg: The Burger Barn"
                onChange={handleOnNameChange}
                value={formData.name}
                aria-label="integration name"
                autoFocus
              />
            </div>

            <Text className="mx-4 mb-4" as="p">
              {`Add or delete ${integrationTypeDescription}. Updates will be pushed to your
              catalogs and kiosks.`}
            </Text>

            {type === 'toast' && formData.locationGuids && (
              <LocationGuidTextFields
                locationGuids={formData.locationGuids}
                onChange={handleOnLocationGuidsChange}
                disabledGuids={settings.restaurantGuids}
              />
            )}

            {type === 'qsr' && formData.sites && (
              <SiteUidTextFields
                sites={formData.sites}
                onChange={handleOnSiteChange}
              />
            )}

            <Footer>
              <Button
                size="large"
                variant="caution"
                className="flex-1"
                onPress={handleDeleteIntegration}
              >
                Delete Integration
              </Button>
              <Button
                size="large"
                variant="primary"
                className="flex-1"
                type="submit"
                isDisabled={!isValid}
              >
                Save
              </Button>
            </Footer>
          </form>
        </Card>
      </PageLayout>
    </Page>
  );
};

export default ProductHubEditGuidIntegrationPage;
